import React, { useMemo } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { aclApi } from '../api/aclApi';
import type { AclNode } from '../types';

export function useAclRulesList() {
  const { t } = useI18n();

  const availableColumns = useMemo(
    () => [
      {
        key: 'listName',
        label: t('acl.columns.listName', 'List Name'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AclNode) => (
          <span className="font-medium text-foreground">{item.listName}</span>
        ),
      },
      {
        key: 'cidr',
        label: t('acl.columns.cidr', 'CIDR (IP/Mask)'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AclNode) => (
          <span className="font-mono text-sm text-foreground">{item.cidr}</span>
        ),
      },
      {
        key: 'nodeType',
        label: t('acl.columns.nodeType', 'Rule Type'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AclNode) => {
          const isAllow = item.nodeType === 'allow';
          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium uppercase ${
                isAllow 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}
            >
              {item.nodeType}
            </span>
          );
        },
      },
      {
        key: 'description',
        label: t('acl.columns.description', 'Description'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AclNode) => (
          <span className="text-sm text-muted-foreground truncate max-w-[200px] inline-block">
            {item.description || '-'}
          </span>
        ),
      },
      {
        key: 'enabled',
        label: t('acl.columns.status', 'Status'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: AclNode) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
              item.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
            }`}
          >
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'actions',
        label: t('common.actions'),
        sortable: false,
        visible: true,
      },
    ],
    [t]
  );

  const fetchData = useMemo(
    () => async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      const result = await aclApi.list({
        ...params,
      });

      return {
        data: result.data,
        pagination: result.pagination,
      };
    },
    []
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'id',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'acl',
  });

  const rules = useMemo(() => data as AclNode[], [data]);

  return {
    rules,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  };
}
